/**
 * Marriage Certificate Approval Page
 * View and manage submitted marriage certificate applications pending approval
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../contexts/PageTitleContext";
import { certificateService } from "../services/api/certificate.service";
import type {
  CertificateApplication,
  County,
} from "../types/certificate.types";
import { UserStatusId } from "../types/user.types";
import BusyOverlay from "../components/common/BusyOverlay";
import {
  ApplicationPermissionId,
  HasPermission,
  ModuleId,
} from "../utils/permissions";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import "../styles/pages/Users.css";

export const WeddingApproval: React.FC = () => {
  const { setPageTitle } = usePageTitle();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [applications, setApplications] = useState<CertificateApplication[]>(
    [],
  );
  const [counties, setCounties] = useState<County[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [countyFilter, setCountyFilter] = useState<number | "all">("all");

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle(
      "Marriage Certificate Approval",
      "Review and approve submitted marriage certificate applications",
    );
    fetchData();
    fetchCounties();
  }, [setPageTitle]);

  const normalizeApplications = (
    payload: unknown,
  ): CertificateApplication[] => {
    const source = payload as
      | CertificateApplication[]
      | {
          data?: CertificateApplication[];
          items?: CertificateApplication[];
          $values?: CertificateApplication[];
        }
      | undefined;

    if (Array.isArray(source)) {
      return source;
    }

    if (Array.isArray(source?.data)) {
      return source.data;
    }

    if (Array.isArray(source?.items)) {
      return source.items;
    }

    if (Array.isArray(source?.$values)) {
      return source.$values;
    }

    return [];
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");

      const data =
        await certificateService.getCertificateApplicationsByMyTask();
      setApplications(normalizeApplications(data));
    } catch (err: any) {
      console.error("Error fetching applications:", err);
      setError(err?.message || "Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  const fetchCounties = async () => {
    try {
      const data = await certificateService.getCounties();
      setCounties(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error("Error fetching counties:", err);
    }
  };

  const filteredApplications = applications.filter((app) => {
    const term = searchTerm.trim().toLowerCase();
    const marriageDateText = app.dateOfWedding
      ? new Date(app.dateOfWedding).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        })
      : "";

    const searchableValues = [
      app.formNo,
      app.groomName,
      app.brideName,
      app.countyName,
      app.email,
      app.placeOfWedding,
      marriageDateText,
      app.status?.name,
      app.statusName,
    ];

    const matchesSearch =
      term.length === 0 ||
      searchableValues.some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(term),
      );

    const matchesCounty =
      countyFilter === "all" || app.countyId === countyFilter;

    // Client-side status filter for approval list
    const matchesStatus =
      app.statusId === UserStatusId.Submitted ||
      app.statusId === UserStatusId.Queried;

    return matchesSearch && matchesCounty && matchesStatus;
  });

  if (!HasPermission(ModuleId.Weddings, ApplicationPermissionId.Approve)) {
    return (
      <div
        className={`dashboard-body ${
          sidebarOpen ? "sidebar-open" : "sidebar-closed"
        }`}
      >
        <Topbar
          onMenuToggle={() => setSidebarOpen((prev) => !prev)}
          sidebarOpen={sidebarOpen}
        />
        <Sidebar isOpen={sidebarOpen} />
        <main className="main-content">
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span>
              You do not have permission to approve marriage certificates
            </span>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((prev) => !prev)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <div className="users-page">
          <BusyOverlay isVisible={loading} message="Loading applications..." />

          <div className="users-content">
            <div className="users-header">
              <input
                type="text"
                className="search-input"
                placeholder="Search by Form No, Groom, Bride, County, Email, Place of Marriage, Date of Marriage, or Status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <select
                className="filter-select"
                value={countyFilter}
                onChange={(e) =>
                  setCountyFilter(
                    e.target.value === "all"
                      ? "all"
                      : parseInt(e.target.value, 10),
                  )
                }
                style={{
                  padding: "0.5rem 1rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "4px",
                  fontSize: "0.875rem",
                  minWidth: "150px",
                }}
              >
                <option value="all">All Counties</option>
                {counties.map((county) => (
                  <option key={county.id} value={county.id}>
                    {county.name}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="alert alert-error">
                <span className="alert-icon">⚠️</span>
                <span>{error}</span>
                <button onClick={() => setError("")} className="alert-close">
                  ✕
                </button>
              </div>
            )}

            <div className="users-table-container">
              {filteredApplications.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state__icon">📋</div>
                  <p className="empty-state__text">
                    {searchTerm || countyFilter !== "all"
                      ? "No applications found matching your criteria"
                      : "No applications pending approval"}
                  </p>
                </div>
              ) : (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Form No</th>
                      <th>Groom</th>
                      <th>Bride</th>
                      <th>County</th>
                      <th>Email</th>
                      <th>Place of Marriage</th>
                      <th>Date of Marriage</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((app) => (
                      <tr key={app.id}>
                        <td>{app.formNo}</td>
                        <td>{app.groomName || "-"}</td>
                        <td>{app.brideName || "-"}</td>
                        <td>{app.countyName || "-"}</td>
                        <td>{app.email || "-"}</td>
                        <td>{app.placeOfWedding || "-"}</td>
                        <td>
                          {app.dateOfWedding
                            ? new Date(app.dateOfWedding).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "2-digit",
                                },
                              )
                            : "-"}
                        </td>
                        <td>
                          <span className="status-badge status-badge--active">
                            {app.status?.name || app.statusName || "Submitted"}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button
                              className="btn-icon"
                              onClick={() =>
                                navigate(`/marriages/approval/${app.id}`)
                              }
                              title="View Detail"
                            >
                              👁️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WeddingApproval;
